import { ClickAwayListener } from '@mui/base';
import { Box, Chip, Divider, Input, Stack, Typography, useTheme } from '@mui/joy';
import { useMediaQuery } from '@mui/material';
import { MagnifyingGlass } from '@phosphor-icons/react';
import { debounce } from 'lodash';
import { ChangeEvent, KeyboardEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import { useAuthUser } from 'auth/hooks';
import { AlternateVersionHelper } from 'core/helpers';
import { isEnterKeyPress, trimFilterText } from 'core/utils';
import { partFinderCatalogPath } from 'partFinder/routes/routePaths';
import { canAccessPartFinder } from 'partFinder/utils';
import {
  InventoryFilter,
  PartDetails,
  PartDetailsTab,
  usePartSearchFilters,
} from 'parts/components';
import { useGetPartsSearch } from 'parts/hooks';
import { partListPath } from 'parts/routes';
import { IPart, IPartsSearchRequestPayload } from 'parts/types';
import { Color, Direction, Level, Orientation, Size, Variant } from 'theme/enums';

import {
  DEBOUNCE_TIME,
  INLINE_PANEL_WIDTH,
  LIST_HEIGHT,
  LIST_WIDTH,
  MIN_CHARS_TO_SEARCH,
  PAGE_SIZE,
  SEARCH_DROPDOWN_Z_INDEX,
} from './constants';
import { PartList } from './PartList';

const PartSearch = () => {
  const [inputValue, setInputValue] = useState('');
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [selectedPart, setSelectedPart] = useState<IPart | null>(null);

  const { filters, criteria, resetFilters, onChangeInventoryFilter } = usePartSearchFilters();

  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isLargeScreen = useMediaQuery(theme.breakpoints.up('lg'));

  const { user } = useAuthUser();

  const isAlternateVersionEnabled = AlternateVersionHelper.isAlternateVersionEnabled({ user });

  const requestPayload = useMemo<IPartsSearchRequestPayload>(() => {
    const payload: IPartsSearchRequestPayload = {
      pageSize: PAGE_SIZE,
      query,
      alternateVersionEnabled: isAlternateVersionEnabled ? 1 : 0,
    };

    if (isAlternateVersionEnabled && criteria) {
      payload.criteria = criteria;
    }

    return payload;
  }, [query, isAlternateVersionEnabled, criteria]);

  const { records, count, hasNextPage, fetchNextPage, isLoading, errorMessage } = useGetPartsSearch(
    requestPayload,
    { enabled: isOpen && query.length >= MIN_CHARS_TO_SEARCH }
  );

  const debouncedSetQuery = useMemo(
    () =>
      debounce((value: string) => {
        setQuery(trimFilterText(value));
        setSelectedPart(null);
      }, DEBOUNCE_TIME),
    []
  );

  useEffect(() => () => debouncedSetQuery.cancel(), [debouncedSetQuery]);

  const onInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setInputValue(e.target.value);
      setIsOpen(true);
      debouncedSetQuery(e.target.value);
    },
    [debouncedSetQuery]
  );

  const onFocus = useCallback(() => {
    setIsOpen(true);
  }, []);

  const onClose = useCallback(() => {
    debouncedSetQuery.cancel();
    setIsOpen(false);
    setQuery('');
    setInputValue('');
    setSelectedPart(null);
    resetFilters();
  }, [debouncedSetQuery, resetFilters]);

  const onGuidedSearchClick = useCallback(() => {
    setIsOpen(false);
    navigate(partFinderCatalogPath(), {
      state: { returnTo: `${location.pathname}${location.search}` },
    });
  }, [navigate, location]);

  const onClickAway = useCallback(() => {
    if (selectedPart && !isLargeScreen) {
      return;
    }

    onClose();
  }, [onClose, selectedPart, isLargeScreen]);

  const onPartDetailsClose = useCallback(() => {
    setSelectedPart(null);
  }, []);

  const onPartInfo = useCallback(
    (part: IPart) => {
      if (
        selectedPart?.number === part.number &&
        selectedPart?.manufacturerId === part.manufacturerId
      ) {
        setSelectedPart(null);

        return;
      }

      setSelectedPart(part);
    },
    [selectedPart]
  );

  const onInputKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (isEnterKeyPress(event) && inputValue) {
        event.preventDefault();
        onClose();
        navigate(partListPath(inputValue));
      }
    },
    [inputValue, navigate, onClose]
  );

  const onSearchIconClick = useCallback(() => {
    if (!inputValue) {
      return;
    }

    onClose();
    navigate(partListPath(inputValue));
  }, [inputValue, navigate, onClose]);

  return (
    <>
      <ClickAwayListener onClickAway={onClickAway}>
        <Box sx={{ position: 'relative', flex: 1 }}>
          <Input
            value={inputValue}
            onChange={onInputChange}
            onFocus={onFocus}
            onKeyDown={onInputKeyDown}
            placeholder={t('labelProductSearch')}
            variant={Variant.PLAIN}
            endDecorator={
              <MagnifyingGlass
                onClick={onSearchIconClick}
                style={{ cursor: inputValue ? 'pointer' : 'default' }}
              />
            }
            sx={{ flex: 1, border: 'none', '--Input-focusedThickness': '0px' }}
          />

          {isOpen && (
            <Box
              sx={{
                position: 'absolute',
                top: 'calc(100% + 8px)',
                left: 0,
                display: 'flex',
                flexDirection: Direction.ROW,
                backgroundColor: 'background.surface',
                borderRadius: '6px',
                border: '1px solid',
                borderColor: 'neutral.300',
                boxShadow: 'md',
                maxHeight: LIST_HEIGHT,
                zIndex: SEARCH_DROPDOWN_Z_INDEX,
              }}
            >
              <Stack spacing={1.5} sx={{ width: LIST_WIDTH, flexShrink: 0, padding: 1.5 }}>
                {canAccessPartFinder(user) && (
                  <Stack spacing={1}>
                    <Typography level={Level.TITLE_SM}>{t('labelSuggestions')}</Typography>
                    <Stack direction={Direction.ROW} spacing={1.5} sx={{ alignItems: 'center' }}>
                      <Chip
                        size={Size.LG}
                        variant={Variant.SOFT}
                        color={Color.PRIMARY}
                        onClick={onGuidedSearchClick}
                      >
                        {t('labelGuidedSearch')}
                      </Chip>
                      <Typography level={Level.BODY_SM}>
                        {t('messageGuidedSearchSuggestion')}
                      </Typography>
                    </Stack>
                  </Stack>
                )}

                {isAlternateVersionEnabled && (
                  <InventoryFilter
                    value={filters.inventory}
                    onChange={onChangeInventoryFilter}
                    isAlternateVersionEnabled={isAlternateVersionEnabled}
                    inline
                  />
                )}

                <PartList
                  records={records}
                  count={count}
                  hasNextPage={hasNextPage}
                  fetchNextPage={fetchNextPage}
                  isLoading={isLoading}
                  errorMessage={errorMessage}
                  selectedPart={selectedPart}
                  onPartInfo={onPartInfo}
                  isAlternateVersionEnabled={isAlternateVersionEnabled}
                  query={query}
                  inputValue={inputValue}
                />
              </Stack>

              {selectedPart && isLargeScreen && (
                <>
                  <Divider orientation={Orientation.VERTICAL} />
                  <Box
                    sx={{
                      width: INLINE_PANEL_WIDTH,
                      height: LIST_HEIGHT,
                      flexShrink: 0,
                      overflowX: 'hidden',
                      overflowY: 'auto',
                      padding: 1.5,
                    }}
                  >
                    <PartDetails
                      part={selectedPart}
                      isOpen
                      onClose={onPartDetailsClose}
                      defaultTab={PartDetailsTab.INVENTORY}
                      inline
                      isAlternateVersionEnabled={isAlternateVersionEnabled}
                    />
                  </Box>
                </>
              )}
            </Box>
          )}
        </Box>
      </ClickAwayListener>

      {selectedPart && !isLargeScreen && (
        <PartDetails
          part={selectedPart}
          isOpen
          onClose={onPartDetailsClose}
          defaultTab={PartDetailsTab.INVENTORY}
          isAlternateVersionEnabled={isAlternateVersionEnabled}
          slidingPanelProps={{ size: Size.LG, autoWidth: false, interactive: true }}
        />
      )}
    </>
  );
};

export default PartSearch;
